import api from '../../api/apiClient';
import type { QuotationPdfFile } from '../admin/admin.api';

/**
 * Proforma bills, bills, receipts and the outstanding report (`BillingController`, V26).
 * Everything sits under /api/accounts — ADMIN and ACCOUNTS only.
 */

export type BillType = 'PROFORMA' | 'INVOICE';
export type BillState = 'ACTIVE' | 'CANCELLED' | 'CONVERTED';
export type Money = number | string;

export interface ApprovedQuote {
  quoteId: number;
  jobId: number;
  jobCode: string | null;
  jobName: string | null;
  clientName: string | null;
  companyId: number | null;
  companyName: string | null;
  quoteDate: string | null;
  approvedAt: string;
  approvedByName: string | null;
  quoteValue: Money;
  gstTotal: Money;
}

export interface BillableMilestone {
  milestoneNumber: number;
  description: string | null;
  paymentPercent: Money | null;
  amount: Money;
  cgstAmount: Money;
  sgstAmount: Money;
  /** V36 — in place of CGST + SGST when the client is in another state. */
  igstAmount: Money;
  totalAmount: Money;
  /** Bill numbers this milestone is already on, or null. */
  invoicedIn: string | null;
  proformaIn: string | null;
}

export interface Billable {
  quote: ApprovedQuote;
  referenceText: string | null;
  /** V36 — the client's state is not the company's: IGST instead of CGST + SGST. */
  interState: boolean;
  milestones: BillableMilestone[];
}

export interface BillSummary {
  id: number;
  billType: BillType;
  status: BillState;
  billNo: string;
  billDate: string;
  financialYear: string;
  companyId: number;
  companyName: string;
  jobId: number;
  jobCode: string | null;
  jobName: string | null;
  /** V36 — the job contact billed: BUILDER / ARCHITECT / OWNER, and its id. */
  clientType: string | null;
  clientId: number | null;
  clientName: string | null;
  amount: Money;
  gstAmount: Money;
  totalAmount: Money;
  received: Money;
  tds: Money;
  discountAmount: Money;
  due: Money;
  /** UNPAID / PARTLY_PAID / PAID on a live bill; the state otherwise. */
  paymentStatus: string;
  proformaId: number | null;
  proformaNo: string | null;
  convertedBillId: number | null;
  convertedBillNo: string | null;
}

export interface BillItem {
  id: number;
  milestoneNumber: number | null;
  description: string;
  paymentPercent: Money | null;
  amount: Money;
  cgstAmount: Money;
  sgstAmount: Money;
  igstAmount: Money;
  totalAmount: Money;
}

export interface BillPayment {
  id: number;
  paymentDate: string;
  amount: Money;
  tdsAmount: Money;
  paymentMode: string | null;
  referenceNo: string | null;
  notes: string | null;
  createdByName: string | null;
  /** Set on an advance received against a proforma; it has a printed receipt. */
  receiptNo: string | null;
  proformaId: number | null;
  proformaNo: string | null;
}

export interface BillDetail {
  bill: BillSummary;
  quoteId: number;
  clientAddress: string | null;
  clientPhone: string | null;
  clientGstNo: string | null;
  /** V36 — the client's state when the bill was raised. */
  clientState: string | null;
  referenceText: string | null;
  paymentRequestNo: string | null;
  paymentRequestDate: string | null;
  workOrderNo: string | null;
  workOrderDate: string | null;
  /** V36 — IGST is charged instead of CGST + SGST (the client is in another state). */
  interState: boolean;
  cgstRate: Money;
  sgstRate: Money;
  igstRate: Money;
  cgstAmount: Money;
  sgstAmount: Money;
  igstAmount: Money;
  discountNote: string | null;
  notes: string | null;
  cancelledAt: string | null;
  cancelReason: string | null;
  items: BillItem[];
  payments: BillPayment[];
}

/**
 * A line of a bill raised from proformas. A milestone line keeps its milestone (its amount may
 * change); a line without one is typed by hand and may be negative ("Less: advance received").
 * Amounts are before GST.
 */
export interface ConvertLine {
  milestoneNumber: number | null;
  description: string | null;
  amount: number;
}

export interface ConvertBody {
  proformaIds: number[];
  billDate?: string;
  /** Left out: the proformas' lines go across unchanged. */
  items?: ConvertLine[];
  notes?: string;
}

export interface OutstandingRow {
  id: number | null;
  name: string;
  bills: number;
  billed: Money;
  received: Money;
  tds: Money;
  paid: Money;
  discount: Money;
  cancelled: Money;
  due: Money;
  details: BillSummary[];
}

export interface Outstanding {
  groupBy: 'CLIENT' | 'COMPANY';
  financialYearId: number | null;
  financialYear: string | null;
  companyId: number | null;
  rows: OutstandingRow[];
  total: OutstandingRow;
}

export interface CreateBillBody {
  billType: BillType;
  quoteId: number;
  billDate?: string;
  items: { milestoneNumber: number | null; description?: string; amount: number }[];
  referenceText?: string;
  paymentRequestNo?: string;
  paymentRequestDate?: string;
  workOrderNo?: string;
  workOrderDate?: string;
  notes?: string;
}

export interface PaymentBody {
  paymentDate: string;
  amount: number;
  tdsAmount?: number;
  paymentMode?: string;
  referenceNo?: string;
  notes?: string;
}

async function data<T>(p: Promise<{ data: { data: T } }>): Promise<T> {
  return (await p).data.data;
}

/** Drops empty filters so the query string carries only what was chosen. */
const params = (o: Record<string, unknown>) =>
  Object.fromEntries(Object.entries(o).filter(([, v]) => v !== '' && v !== null && v !== undefined));

export const billingApi = {
  approvedQuotes: (jobId?: number) => data<ApprovedQuote[]>(api.get('/api/accounts/approved-quotes', { params: params({ jobId }) })),
  billable: (quoteId: number) => data<Billable>(api.get(`/api/accounts/approved-quotes/${quoteId}/billable`)),
  list: (f: { type?: BillType | ''; financialYearId?: number | ''; companyId?: number | ''; clientId?: number; jobId?: number }) =>
    data<BillSummary[]>(api.get('/api/accounts/bills', { params: params(f) })),
  get: (id: number) => data<BillDetail>(api.get(`/api/accounts/bills/${id}`)),
  create: (body: CreateBillBody) => data<BillDetail>(api.post('/api/accounts/bills', body)),
  convert: (id: number, billDate?: string) => data<BillDetail>(api.post(`/api/accounts/bills/${id}/convert`, { billDate })),
  /** One bill from one or more open proformas of the same quotation, its lines adjusted if needed. */
  convertMany: (body: ConvertBody) => data<BillDetail>(api.post('/api/accounts/bills/convert', body)),
  cancel: (id: number, reason: string) => data<BillDetail>(api.post(`/api/accounts/bills/${id}/cancel`, { reason })),
  discount: (id: number, amount: number, note?: string) =>
    data<BillDetail>(api.put(`/api/accounts/bills/${id}/discount`, { amount, note })),
  addPayment: (id: number, body: PaymentBody) => data<BillDetail>(api.post(`/api/accounts/bills/${id}/payments`, body)),
  deletePayment: (id: number, paymentId: number) => data<BillDetail>(api.delete(`/api/accounts/bills/${id}/payments/${paymentId}`)),
  outstanding: (f: { financialYearId?: number | ''; companyId?: number | ''; groupBy?: 'CLIENT' | 'COMPANY' }) =>
    data<Outstanding>(api.get('/api/accounts/outstanding', { params: params(f) })),
};

/** The bill's PDF as an object URL for the viewer; the caller revokes it. */
export async function fetchBillPdf(id: number, billNo: string): Promise<QuotationPdfFile> {
  const res = await api.get<Blob>(`/api/accounts/bills/${id}/pdf`, { responseType: 'blob' }).catch(async (error: any) => {
    // A failed PDF arrives as a Blob too; read its JSON message so the toast says what went wrong.
    const body = error?.response?.data;
    if (body instanceof Blob) {
      try {
        const parsed = JSON.parse(await body.text());
        error.userMessage = parsed?.message || parsed?.error || error.userMessage;
      } catch {
        // not JSON — keep the interceptor's message
      }
    }
    throw error;
  });
  return {
    url: URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' })),
    filename: `${billNo.replace(/[^A-Za-z0-9._-]+/g, '-')}.pdf`,
  };
}

/** An advance's printed receipt as an object URL for the viewer; the caller revokes it. */
export async function fetchReceiptPdf(billId: number, paymentId: number, receiptNo: string): Promise<QuotationPdfFile> {
  const res = await api.get<Blob>(`/api/accounts/bills/${billId}/payments/${paymentId}/receipt-pdf`, { responseType: 'blob' });
  return {
    url: URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' })),
    filename: `${receiptNo.replace(/[^A-Za-z0-9._-]+/g, '-')}.pdf`,
  };
}

/** ₹ 1,23,456.00 */
export const rupees = (value: Money | null | undefined) =>
  `₹ ${Number(value ?? 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const PAYMENT_MODES = ['NEFT', 'RTGS', 'IMPS', 'UPI', 'CHEQUE', 'CASH', 'OTHER'];
